import React from 'react';
import { Question, StudentAnswer } from '../../types/exam';

interface ExamQuestionProps {
  question: Question;
  answer?: StudentAnswer;
  onChange: (answer: StudentAnswer) => void;
}

export const ExamQuestion: React.FC<ExamQuestionProps> = ({
  question,
  answer,
  onChange,
}) => {
  const getOptionIdentity = (option: NonNullable<Question['options']>[number], optionIndex: number) =>
    String(option.id || option.optionId || option.key || option.text || optionIndex);

  const getOptionAnswerValue = (option: NonNullable<Question['options']>[number], optionIndex: number) =>
    String(option.text || option.id || option.optionId || option.key || optionIndex);

  const isOptionSelected = (option: NonNullable<Question['options']>[number], optionIndex: number) => {
    if (!answer || answer.answer === undefined || answer.answer === null || answer.answer === '') return false;
    const selectedValue = String(answer.answer);
    return selectedValue === getOptionAnswerValue(option, optionIndex) ||
      selectedValue === getOptionIdentity(option, optionIndex) ||
      selectedValue === String(optionIndex);
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'EASY':
        return 'text-green-600 bg-green-50';
      case 'MEDIUM':
        return 'text-yellow-600 bg-yellow-50';
      case 'HARD':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const handleMCQChange = (optionIndex: number) => {
    const option = question.options?.[optionIndex];
    if (option) {
      onChange({
        questionId: question.questionId,
        answer: getOptionAnswerValue(option, optionIndex),
      });
    }
  };

  const handleTextAnswer = (text: string) => {
    onChange({
      questionId: question.questionId,
      answer: text,
    });
  };

  const handleTrueFalse = (value: boolean) => {
    onChange({
      questionId: question.questionId,
      answer: value,
    });
  };

  return (
    <div className="space-y-6">
      {/* Question Header */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold text-gray-800">
            {question.questionText}
          </h2>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium px-2 py-1 rounded ${getDifficultyColor(question.difficulty)}`}>
              {question.difficulty}
            </span>
            <span className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded">
              {question.marks}M
            </span>
          </div>
        </div>
      </div>

      {/* Question Content */}
      <div className="space-y-4">
        {/* MCQ */}
        {question.questionType === 'MCQ' && question.options && (
          <div className="space-y-2">
            {question.options.map((option, idx) => (
              <label
                key={getOptionIdentity(option, idx)}
                className="flex items-center p-3 border-2 border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer transition"
              >
                <input
                  type="radio"
                  name={`question_${question.questionId}`}
                  value={getOptionAnswerValue(option, idx)}
                  checked={isOptionSelected(option, idx)}
                  onChange={() => handleMCQChange(idx)}
                  className="w-4 h-4 text-blue-500 border-gray-300 focus:ring-blue-500"
                />
                <span className="ml-3 text-gray-800">{option.text}</span>
              </label>
            ))}
          </div>
        )}

        {/* True/False */}
        {question.questionType === 'TRUE_FALSE' && (
          <div className="space-y-2">
            <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer transition">
              <input
                type="radio"
                name={`question_${question.questionId}`}
                checked={answer?.answer === true}
                onChange={() => handleTrueFalse(true)}
                className="w-4 h-4 text-blue-500 border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-3 text-gray-800 font-medium">True</span>
            </label>
            <label className="flex items-center p-3 border-2 border-gray-200 rounded-lg hover:border-blue-300 cursor-pointer transition">
              <input
                type="radio"
                name={`question_${question.questionId}`}
                checked={answer?.answer === false}
                onChange={() => handleTrueFalse(false)}
                className="w-4 h-4 text-blue-500 border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-3 text-gray-800 font-medium">False</span>
            </label>
          </div>
        )}

        {/* Short Answer / Essay */}
        {(question.questionType === 'SHORT_ANSWER' || question.questionType === 'ESSAY') && (
          <textarea
            value={answer?.answer as string || ''}
            onChange={(e) => handleTextAnswer(e.target.value)}
            rows={question.questionType === 'ESSAY' ? 6 : 3}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-blue-200 focus:ring-2 resize-none"
            placeholder={`Enter your ${question.questionType === 'ESSAY' ? 'essay' : 'answer'} here...`}
          />
        )}

        {/* Matching */}
        {question.questionType === 'MATCHING' && question.options && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">Left Column</h4>
              <div className="space-y-2">
                {question.options
                  .filter((_, idx) => idx % 2 === 0)
                  .map((option, idx) => (
                    <div key={getOptionIdentity(option, idx * 2)} className="p-2 bg-gray-50 rounded">
                      {option.text}
                    </div>
                  ))}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-700 mb-2">Right Column</h4>
              <div className="space-y-2">
                {question.options
                  .filter((_, idx) => idx % 2 === 1)
                  .map((option, idx) => (
                    <div key={getOptionIdentity(option, idx * 2 + 1)} className="p-2 bg-gray-50 rounded">
                      {option.text}
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Answer Status */}
      {answer && (
        <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
          ✓ Your answer has been saved
        </div>
      )}
    </div>
  );
};

export default ExamQuestion;
